/**
 * The one site footer, matched to yoursmartstack.com.
 *
 * It used to be a light band — muted background, three columns of its own
 * choosing, a "Caren Glasser · Your Tech Evangelist" line. The storefront ends
 * on a deep navy block with four columns and a centred copyright, and crossing
 * from one to the other in the same session made them read as two products.
 * This is that footer, rebuilt here rather than approximated: the same
 * background, the same tan column headings, the same order, the same links.
 *
 * The navy is `bg-primary`, not a literal. The storefront calls it `--atlas`
 * and defines it as oklch(32% .08 255); index.css defines --primary as
 * oklch(0.32 0.08 255) and calls it "Atlas navy as primary". Same colour, two
 * names, and using the token means the app cannot drift from itself.
 *
 * TWO DELIBERATE DIFFERENCES FROM THE STOREFRONT, both worth knowing before
 * somebody "fixes" them back:
 *
 *   1. Links are 44px tall. The storefront stacks ~20px links on a 9px gap,
 *      which is about 29px of rhythm; this uses padding to reach the 44px
 *      floor the rest of this site holds to, and drops the gap so the spacing
 *      lands close. Type, colour, order and columns are identical — the
 *      footer is simply about 45px taller. The documented audience is women
 *      55+ on phones, and a 20px tap target is the one thing here worth
 *      failing parity over.
 *   2. "Suggest an AI Tool" opens the Google Form. On the storefront it points
 *      at this app's homepage, which from inside this app is a link to where
 *      you already are. The form is what that link is actually for, and it was
 *      the only route a visitor had to tell us about a missing tool.
 *
 * The year is computed. A stale year on the pages people read longest is a
 * small thing that reads as abandonment.
 */

import { useEffect, useState } from "react";
import { Link } from "wouter";
import { ArrowUp } from "lucide-react";

/** Where the storefront's own footer sends people. */
const STOREFRONT = "https://yoursmartstack.com";

type FooterLink = { label: string; href: string; external?: boolean };

const COLUMNS: { heading: string; links: FooterLink[] }[] = [
  {
    heading: "The Roles Map",
    /* Internal, so these stay client-side navigations. The storefront writes
       them as absolute https://aitools.yoursmartstack.com URLs because from
       there they are; from here that would be a full page load to ourselves. */
    links: [
      { label: "AI Roles Map", href: "/" },
      { label: "Resource Center", href: "/resources" },
      { label: "Prompt Library", href: "/prompts" },
      { label: "My Stack", href: "/my-stack" },
    ],
  },
  {
    heading: "Products",
    /* Anchors on the storefront's single long page. Absolute, and external:
       there is no /#plus in this app and a wouter Link would 404. */
    links: [
      { label: "Your Smart Stack +", href: `${STOREFRONT}/#plus`, external: true },
      { label: "Pricing", href: `${STOREFRONT}/#pricing`, external: true },
      { label: "App Store", href: `${STOREFRONT}/#store`, external: true },
    ],
  },
  {
    heading: "More",
    links: [
      { label: "Smart Stack Videos", href: "https://videos.yoursmartstack.com/", external: true },
      { label: "Smart Stack Blog", href: "https://carenglasser.com/blog", external: true },
      { label: "Suggest an AI Tool", href: "https://forms.gle/b6L4VhKyyZM9cNbV9", external: true },
    ],
  },
];

/** Type, colour and hover, identical for both link kinds. */
const LINK_CLASS =
  "inline-flex items-center min-h-11 py-1 text-[14.5px] text-[#d8d2c9] " +
  "hover:text-white hover:underline underline-offset-4 transition-colors";

export default function SiteFooter() {
  const year = new Date().getFullYear();

  return (
    <>
      {/* No top margin. The navy is already a hard edge against the page, so
          a gap above it read as the page ending twice — content, a band of
          cream, then the footer. The storefront's sits flush too. Spacing
          inside the block is pt-14's job. */}
      <footer className="bg-primary pt-14 pb-8">
        <div className="container">
          {/* 1.4fr on the wordmark column, matching the storefront: two
              columns at 960px, one at 620px. */}
          <div className="grid grid-cols-1 min-[620px]:grid-cols-2 min-[960px]:grid-cols-[1.4fr_1fr_1fr_1fr] gap-9">
            <div>
              <Link
                href="/"
                className="font-['Fraunces'] text-xl font-bold tracking-tight text-[#fffefc] whitespace-nowrap"
              >
                Your Smart Stack
              </Link>
            </div>

            {COLUMNS.map(col => (
              <div key={col.heading}>
                <h2 className="font-semibold uppercase text-[11.5px] tracking-[0.14em] text-[oklch(72%_0.06_70)] mb-3">
                  {col.heading}
                </h2>
                <ul className="grid">
                  {col.links.map(link => (
                    <li key={link.label}>
                      {link.external ? (
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={LINK_CLASS}
                        >
                          {link.label}
                        </a>
                      ) : (
                        <Link href={link.href} className={LINK_CLASS}>
                          {link.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Centred, not space-between. One child pushed to the left edge of a
              1080px block reads as a mistake, which is why the storefront has
              an `is-solo` modifier for exactly this case. */}
          <div className="mt-10 pt-5 border-t border-[#3d5375] text-center text-[13px] text-[#b9b2a8]">
            &copy; {year} Your Smart Stack. All rights reserved.
          </div>
        </div>
      </footer>

      <BackToTop />
    </>
  );
}

/**
 * The floating "back to top" control, also from the storefront.
 *
 * z-40, so it passes under the sticky header rather than over it.
 *
 * Hidden with `visibility`, not opacity alone. An opacity-0 button is still
 * focusable and still clickable, so a keyboard user would land on an invisible
 * control somewhere above the footer with no way to tell what it was.
 */
function BackToTop() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <button
      type="button"
      aria-label="Back to top"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className={`fixed right-4 bottom-4 sm:right-6 sm:bottom-6 z-40 grid place-items-center
        w-[42px] h-[42px] sm:w-[46px] sm:h-[46px] rounded-full bg-primary text-[#fffefc]
        shadow-[0_8px_24px_rgba(36,59,99,0.28)] transition-all duration-200
        hover:brightness-125 focus-visible:outline-none focus-visible:ring-2
        focus-visible:ring-primary focus-visible:ring-offset-2 ${
          visible ? "opacity-100 visible translate-y-0" : "opacity-0 invisible translate-y-2"
        }`}
    >
      <ArrowUp className="w-5 h-5" aria-hidden="true" />
    </button>
  );
}
